import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, catchError, finalize, shareReplay, switchMap, tap, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { StorageService } from '../services/storage';
import { AuthService } from '../services/auth';
import { ToastService } from '../services/toast';

// One refresh at a time for the whole app: concurrent 401s (e.g. several requests fired on a
// page load with an expired token) share this in-flight call instead of racing each other —
// the backend rotates refresh tokens, so a second parallel refresh would invalidate the first.
// Exported so the shell's idle-session watchdog uses the SAME mutex (never a parallel refresh).
let refreshInFlight$: Observable<any> | null = null;

export const refreshOnce = (auth: AuthService): Observable<any> => {
  if (!refreshInFlight$) {
    refreshInFlight$ = auth.refreshToken().pipe(
      tap((res: any) => {
        // The API can return 200 with success:false — treat that as a failed refresh.
        if (res?.success === false || !res?.data?.token) {
          throw new Error(res?.message || 'Token refresh failed');
        }
        auth.saveAuth(res);
      }),
      finalize(() => (refreshInFlight$ = null)),
      shareReplay(1),
    );
  }
  return refreshInFlight$;
};

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const storage = inject(StorageService);
  const auth = inject(AuthService);
  const toast = inject(ToastService);
  const router = inject(Router);
  const token = storage.getLocal('token');

  // 🔐 Attach token
  if (token) {
    req = req.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
    });
  }

  return next(req).pipe(
    catchError((error) => {
      // 🔥 Handle expired token
      if (error.status === 401) {
        const refreshToken = storage.getLocal('refreshToken');
        const hasToken = !!storage.getLocal('token');

        // ❌ Not logged in (e.g. a failed login attempt), nothing to refresh with, or the
        //    failing call is the refresh/logout itself (refreshing those would loop) →
        //    let the caller handle the 401.
        if (
          !refreshToken ||
          !hasToken ||
          req.url.includes('/Login/RefreshToken') ||
          req.url.includes('/Login/Logout')
        ) {
          return throwError(() => error);
        }

        // 🔁 Refresh (shared across concurrent 401s), then retry the original request.
        return refreshOnce(auth).pipe(
          // ❌ Refresh failed → end the session. This catchError sits BEFORE the retry
          //    switchMap on purpose: a retry that fails (e.g. 500) must NOT log the user out.
          catchError((err) => {
            // errorInterceptor already toasted/cleaned for a 401 on the refresh call itself;
            // finish locally only if the session is still around (400 / network / bad envelope).
            if (storage.getLocal('token')) {
              // Best-effort backend revoke while the refresh token is still in storage.
              auth.logout().subscribe({ next: () => {}, error: () => {} });
              storage.clearAll();
              toast.error('Session expired. Please login again');
              router.navigate(['/login']);
            }
            return throwError(() => err);
          }),
          switchMap(() => {
            // ✅ Retry original request with the new token
            const newReq = req.clone({
              setHeaders: { Authorization: `Bearer ${auth.getToken()}` },
            });
            return next(newReq);
          }),
        );
      }

      return throwError(() => error);
    }),
  );
};
